import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Eye, Phone, Megaphone, Search, CheckCircle2, BarChart2,
  TrendingUp, User as UserIcon, Plus, Users, ArrowRight, Target
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function useCounter(target, duration = 900) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) cancelAnimationFrame(ref.current);
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * target));
      if (p < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => ref.current && cancelAnimationFrame(ref.current);
  }, [target, duration]);
  return value;
}

function getTone(value, thresholds) {
  if (value >= thresholds.good) return "text-green-400";
  if (value >= thresholds.mid) return "text-gold";
  if (value > 0) return "text-orange-400";
  return "text-cbm-muted";
}

function KpiCard({ icon: Icon, label, value, suffix = "", tone = "text-gold", testId }) {
  const animated = useCounter(Number(value) || 0);
  return (
    <div className="cbm-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(201,162,39,0.12)", border: "1px solid rgba(201,162,39,0.25)" }}>
          <Icon size={14} className="text-gold" />
        </div>
        <p className="cbm-label">{label}</p>
      </div>
      <p className={`font-outfit ${tone}`} style={{ fontSize: "2.25rem", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1 }}>
        {animated}{suffix}
      </p>
    </div>
  );
}

function ActivationMessage({ stats, onNavigate }) {
  const { opps_total, cherche_total, cherche_resolved, profile_complete } = stats;

  let message, cta, path, testId;
  if (!profile_complete) {
    message = "Votre profil n'est pas encore complet.";
    cta = "Compléter mon profil";
    path = "/mon-profil";
    testId = "msg-profile-incomplete";
  } else if (opps_total === 0) {
    message = "Publiez une demande pour commencer à générer des opportunités.";
    cta = "Poster une annonce";
    path = "/poster-annonce";
    testId = "msg-no-opps";
  } else if (cherche_total > 0 && cherche_resolved === 0) {
    message = "Vos demandes sont visibles, mais aucune n'est encore marquée comme trouvée.";
    cta = "Voir mes annonces";
    path = "/mon-profil";
    testId = "msg-pending";
  } else {
    message = "Votre activité est bonne. Continuez à interagir avec les membres.";
    cta = "Voir l'annuaire";
    path = "/annuaire";
    testId = "msg-active";
  }

  return (
    <div
      className="rounded-xl p-5 flex items-center justify-between flex-wrap gap-3"
      style={{
        background: "linear-gradient(135deg, rgba(201,162,39,0.10), rgba(201,162,39,0.02))",
        border: "1px solid rgba(201,162,39,0.28)",
      }}
      data-testid={testId}
    >
      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={14} className="text-gold" />
          <p className="cbm-label">Recommandation</p>
        </div>
        <p className="text-white text-sm leading-snug font-medium">{message}</p>
      </div>
      <button
        onClick={() => onNavigate(path)}
        className="cbm-btn-primary text-sm"
        data-testid="activation-cta"
      >
        {cta} <ArrowRight size={13} />
      </button>
    </div>
  );
}

function ChallengeCard({ stats, onNavigate }) {
  const score = stats.activity_score || 0;
  const tiers = [
    { threshold: 30, label: "Profil à activer" },
    { threshold: 60, label: "En développement" },
    { threshold: 80, label: "Visible" },
    { threshold: 100, label: "Très actif" },
  ];
  const next = tiers.find(t => score < t.threshold);

  // CTA logic — always different priority than the Recommendation card
  let cta, path;
  if (!stats.profile_complete) {
    cta = "Compléter mon profil";
    path = "/mon-profil";
  } else if (stats.opps_total === 0) {
    cta = "Poster une annonce";
    path = "/poster-annonce";
  } else {
    cta = "Voir les opportunités";
    path = "/opportunites";
  }

  let title, message;
  if (!next) {
    title = "Objectif atteint";
    message = "Vous êtes au niveau Très actif. Continuez à interagir.";
  } else {
    const delta = next.threshold - score;
    title = `Objectif : atteindre ${next.threshold} points`;
    message = `Encore +${delta} points pour atteindre le niveau ${next.label}.`;
  }

  const progress = next ? Math.min(100, Math.round((score / next.threshold) * 100)) : 100;

  return (
    <div
      className="cbm-card p-5 mb-5 animate-fade-in-up"
      data-testid="challenge-card"
      style={{
        borderColor: "rgba(201,162,39,0.55)",
        borderLeftWidth: "3px",
        borderLeftColor: "#C9A227",
      }}
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(201,162,39,0.18)", border: "1px solid rgba(201,162,39,0.45)" }}>
            <Target size={16} className="text-gold" />
          </div>
          <div>
            <p className="cbm-label mb-1" style={{ color: "#C9A227" }}>Défi de la semaine</p>
            <p className="font-outfit font-bold text-base" data-testid="challenge-title" style={{ color: "#FFFFFF", letterSpacing: "-0.01em" }}>
              {title}
            </p>
            <p className="text-sm mt-1 font-medium" data-testid="challenge-message" style={{ color: "#E5ECF8" }}>
              {message}
            </p>
          </div>
        </div>
        <button
          onClick={() => onNavigate(path)}
          className="cbm-btn-primary text-sm flex-shrink-0"
          data-testid="challenge-cta"
        >
          {cta} <ArrowRight size={13} />
        </button>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(184,196,224,0.12)" }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${progress}%`, background: "#C9A227", boxShadow: "0 0 8px rgba(201,162,39,0.5)" }}
          data-testid="challenge-progress"
        />
      </div>
    </div>
  );
}

export default function MesStatistiques() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("total");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/my-stats?period=${period}`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading || !stats) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="cbm-skeleton h-8 w-60 mb-6 rounded-lg" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="cbm-skeleton rounded-xl" style={{ height: 130 }} />)}
        </div>
      </div>
    );
  }

  const periods = [
    { id: "7d", label: "7 jours" },
    { id: "30d", label: "30 jours" },
    { id: "total", label: "Total" },
  ];

  const scoreColor =
    stats.activity_score > 80 ? "#22c55e"
    : stats.activity_score > 60 ? "#C9A227"
    : stats.activity_score > 30 ? "#eab308"
    : "#f97316";

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in" data-testid="my-stats-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}>
              <BarChart2 size={18} className="text-gold" />
            </div>
            <h1 className="cbm-section-title">Mes statistiques</h1>
          </div>
          <p className="text-cbm-muted text-sm ml-[52px]">Mesurez l'impact de votre présence dans le club</p>
        </div>
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1 border border-white/10" data-testid="stats-period-selector">
          {periods.map(p => (
            <button
              key={p.id}
              data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                period === p.id ? "bg-gold/15 text-gold" : "text-cbm-secondary hover:text-white"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Activity Score */}
      <div className="cbm-card p-5 mb-5 animate-fade-in-up" data-testid="activity-score-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="cbm-label mb-1">Score d'activité</p>
            <div className="flex items-center gap-3">
              <p className="font-outfit text-white" style={{ fontSize: "2rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1 }} data-testid="activity-score-value">
                {stats.activity_score}<span className="text-cbm-muted" style={{ fontSize: "1rem", fontWeight: 500 }}>/100</span>
              </p>
              <span className="cbm-badge" style={{ background: `${scoreColor}20`, border: `1px solid ${scoreColor}66`, color: scoreColor }} data-testid="activity-score-label">
                {stats.score_label}
              </span>
            </div>
          </div>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(184,196,224,0.12)" }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${stats.activity_score}%`, background: scoreColor, boxShadow: `0 0 10px ${scoreColor}55` }}
          />
        </div>
      </div>

      {/* Weekly Challenge */}
      <ChallengeCard stats={stats} onNavigate={navigate} />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        <KpiCard
          icon={Eye}
          label="Vues de mon profil"
          value={stats.profile_views}
          tone={getTone(stats.profile_views, { good: 20, mid: 5 })}
          testId="kpi-profile-views"
        />
        <KpiCard
          icon={Phone}
          label="Clics sur mes contacts"
          value={stats.contact_clicks}
          tone={getTone(stats.contact_clicks, { good: 10, mid: 3 })}
          testId="kpi-contact-clicks"
        />
        <KpiCard
          icon={Megaphone}
          label="Annonces publiées"
          value={stats.opps_total}
          tone={getTone(stats.opps_total, { good: 5, mid: 1 })}
          testId="kpi-opps-total"
        />
        <KpiCard
          icon={Search}
          label="Demandes « Je cherche »"
          value={stats.cherche_total}
          tone={getTone(stats.cherche_total, { good: 3, mid: 1 })}
          testId="kpi-cherche-total"
        />
        <KpiCard
          icon={CheckCircle2}
          label="Demandes trouvées"
          value={stats.cherche_resolved}
          tone={getTone(stats.cherche_resolved, { good: 2, mid: 1 })}
          testId="kpi-cherche-resolved"
        />
        <KpiCard
          icon={BarChart2}
          label="Taux de résolution"
          value={stats.cherche_total > 0 ? stats.resolution_rate : 0}
          suffix={stats.cherche_total > 0 ? "%" : ""}
          tone={getTone(stats.resolution_rate, { good: 60, mid: 30 })}
          testId="kpi-resolution-rate"
        />
      </div>

      {/* Activation message */}
      <div className="mb-6 animate-fade-in-up-delay-1">
        <ActivationMessage stats={stats} onNavigate={navigate} />
      </div>

      {/* Bottom CTAs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="bottom-ctas">
        <button
          data-testid="cta-complete-profile"
          onClick={() => navigate("/mon-profil")}
          className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
            <UserIcon size={16} className="text-gold" />
          </div>
          <p className="text-white font-semibold text-sm">Compléter mon profil</p>
          <p className="text-cbm-muted text-xs mt-0.5">Améliorez votre visibilité</p>
        </button>
        <button
          data-testid="cta-post-opp"
          onClick={() => navigate("/poster-annonce")}
          className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
            <Plus size={16} className="text-gold" />
          </div>
          <p className="text-white font-semibold text-sm">Poster une annonce</p>
          <p className="text-cbm-muted text-xs mt-0.5">Créez des opportunités</p>
        </button>
        <button
          data-testid="cta-directory"
          onClick={() => navigate("/annuaire")}
          className="cbm-card p-4 text-left hover:border-gold/40 transition-all"
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(201,162,39,0.12)" }}>
            <Users size={16} className="text-gold" />
          </div>
          <p className="text-white font-semibold text-sm">Voir l'annuaire</p>
          <p className="text-cbm-muted text-xs mt-0.5">Explorez les membres du club</p>
        </button>
      </div>
    </div>
  );
}
